import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, IconButton, Avatar, Tooltip, alpha,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Toolbar,
} from '@mui/material';
import {
    Logout as LogoutIcon,
    ArrowBack as ArrowBackIcon,
    GridView as GridViewIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCategory } from '../context/CategoryContext';
import { useSocket } from '../context/SocketContext';
import { CATEGORIES, ALL_MENU_ITEMS, type CategoryDef } from '../config/categoryConfig';
import { getNewPaymentsCount } from '../services/api';
import yannLogo from '../assets/logo/logo_extracted.png';

const SIDEBAR_W = 260;

const CategoryHub: React.FC = () => {
    const navigate = useNavigate();
    const { logout, admin } = useAuth();
    const { setActiveCategory } = useCategory();
    const { socket } = useSocket();
    const [tx, setTx] = useState<{ phase: number; cat: CategoryDef; rect: DOMRect } | null>(null);
    const [newPaymentsCount, setNewPaymentsCount] = useState(0);

    // Finance "new payments" badge: fetch current unread count, then live-increment on
    // each incoming payment notification (link === '/transactions').
    useEffect(() => {
        getNewPaymentsCount().then(setNewPaymentsCount).catch(() => {});
    }, []);

    useEffect(() => {
        if (!socket) return;
        const onNotif = (data: any) => {
            if (data?.link === '/transactions') setNewPaymentsCount((c) => c + 1);
        };
        socket.on('new_admin_notification', onNotif);
        return () => { socket.off('new_admin_notification', onNotif); };
    }, [socket]);

    const handleCategoryClick = (cat: CategoryDef, e: React.MouseEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setTx({ phase: 0, cat, rect });
    };

    useEffect(() => {
        if (!tx) return;
        if (tx.phase === 0) {
            requestAnimationFrame(() => requestAnimationFrame(() => setTx(p => p && ({ ...p, phase: 1 }))));
        } else if (tx.phase === 1) {
            const t = setTimeout(() => setTx(p => p && ({ ...p, phase: 2 })), 550);
            return () => clearTimeout(t);
        } else if (tx.phase === 2) {
            const t = setTimeout(() => { setActiveCategory(tx.cat.key); navigate(tx.cat.firstRoute); }, 500);
            return () => clearTimeout(t);
        }
    }, [tx?.phase]);

    const catItems = tx ? ALL_MENU_ITEMS.filter(m => m.category === tx.cat.key) : [];
    const morphed = tx && tx.phase >= 1;
    const showContent = tx && tx.phase >= 2;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)', display: 'flex', flexDirection: 'column' }}>

            {/* ── Header ───────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2.5, sm: 4, md: 6 }, py: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fff', opacity: tx ? 0 : 1, transition: 'opacity 0.35s ease' }}>
                <Box component="img" src={yannLogo} alt="YANN" sx={{ height: 36, width: 'auto' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                        <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 600, lineHeight: 1.2 }}>Super Admin</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>{admin?.email || 'admin@scankaro.com'}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563ea', fontWeight: 700, width: 40, height: 40, fontSize: '0.875rem', border: '1px solid #2563ea' }}>SA</Avatar>
                    <Tooltip title="Logout"><IconButton onClick={logout} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: alpha('#ef4444', 0.08) } }}><LogoutIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
            </Box>

            {/* ── Hero ─────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 4, md: 6 }, pb: 6, opacity: tx ? 0 : 1, transition: 'opacity 0.3s ease' }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' }, letterSpacing: '-0.02em', mb: 1 }}>Command Center</Typography>
                    <Typography variant="body1" sx={{ color: '#64748b', fontSize: { xs: '0.875rem', md: '1rem' }, maxWidth: 480, mx: 'auto' }}>Select a module to manage your transport operations</Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: 1100 }}>
                    {CATEGORIES.map((cat, index) => (
                        <Box key={cat.key} onClick={(e) => !tx && handleCategoryClick(cat, e)}
                            sx={{
                                position: 'relative', borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                                bgcolor: '#fff', border: '1px solid #e2e8f0', p: { xs: 2.5, sm: 3 },
                                minHeight: { xs: 140, sm: 170 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                transition: 'all 0.35s cubic-bezier(0.25,0.46,0.45,0.94)',
                                animation: `categoryFadeIn 0.5s ease-out ${index * 0.07}s both`,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                '&:hover': { transform: 'translateY(-5px)', borderColor: '#cbd5e1', boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)', '& .cat-icon-wrap': { transform: 'scale(1.1)' }, '& .cat-glow': { opacity: 1 }, '& .cat-arrow': { opacity: 1, transform: 'translateX(0)' } },
                            }}
                        >
                            <Box className="cat-glow" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: cat.gradient, opacity: 0, transition: 'opacity 0.35s ease' }} />
                            <Box className="cat-icon-wrap" sx={{ width: 52, height: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: cat.iconBg, color: cat.iconColor, mb: 2, transition: 'all 0.35s ease' }}>{cat.icon}</Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: { xs: '1rem', sm: '1.05rem' }, lineHeight: 1.3, mb: 0.5 }}>{cat.label}</Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1.4 }}>{cat.subtitle}</Typography>
                            </Box>
                            {cat.key === 'finance' && newPaymentsCount > 0 ? (
                                <Tooltip title={`${newPaymentsCount} new payment${newPaymentsCount === 1 ? '' : 's'}`}>
                                    <Box sx={{
                                        position: 'absolute', top: 14, right: 14,
                                        minWidth: 22, height: 22, px: 0.75, borderRadius: '11px',
                                        bgcolor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(239,68,68,0.45)',
                                        animation: 'badgePulse 1.8s ease-in-out infinite',
                                    }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.72rem', lineHeight: 1 }}>
                                            {newPaymentsCount > 99 ? '99+' : newPaymentsCount}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            ) : (
                                <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#f1f5f9', borderRadius: 1, px: 1, py: 0.3 }}>
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.7rem' }}>{cat.itemCount} {cat.itemCount === 1 ? 'item' : 'items'}</Typography>
                                </Box>
                            )}
                            <Typography className="cat-arrow" sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0, transform: 'translateX(-8px)', transition: 'all 0.35s ease', color: cat.iconColor, fontSize: '1.2rem' }}>→</Typography>
                        </Box>
                    ))}
                </Box>
                <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 6, fontWeight: 500, letterSpacing: '0.05em' }}>YANN TRANSPORT • ADMIN PANEL v2.0</Typography>
            </Box>

            {/* ═══════════════════════════════════════════════════════════
                TRANSITION OVERLAY — pixel-perfect match with real sidebar
               ═══════════════════════════════════════════════════════════ */}
            {tx && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}>
                    {/* Background */}
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#f8fafc', opacity: morphed ? 1 : 0, transition: 'opacity 0.45s ease' }} />

                    {/* ── Morphing card → sidebar (EXACT match with DashboardLayout drawer) ── */}
                    <Box sx={{
                        position: 'fixed',
                        top: morphed ? 0 : tx.rect.top,
                        left: morphed ? 0 : tx.rect.left,
                        width: morphed ? SIDEBAR_W : tx.rect.width,
                        height: morphed ? '100vh' : tx.rect.height,
                        borderRadius: morphed ? 0 : '12px',
                        bgcolor: '#ffffff',
                        boxShadow: morphed ? 'none' : '0 8px 30px rgba(0,0,0,0.12)',
                        borderRight: morphed ? '1px solid #e2e8f0' : 'none',
                        transition: 'all 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: 'hidden', zIndex: 10000,
                    }}>
                        {/* Card face — fades out */}
                        <Box sx={{ position: 'absolute', inset: 0, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', opacity: morphed ? 0 : 1, transition: 'opacity 0.2s ease' }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: tx.cat.iconBg, color: tx.cat.iconColor }}>{tx.cat.icon}</Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', mb: 0.5 }}>{tx.cat.label}</Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{tx.cat.subtitle}</Typography>
                            </Box>
                        </Box>

                        {/* ── Real sidebar replica — fades in ─────────────── */}
                        <Box sx={{ opacity: showContent ? 1 : 0, transition: 'opacity 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* Logo toolbar — EXACT match: Toolbar with px:[2], height 64 */}
                            <Toolbar sx={{ px: [2], display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '64px !important' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                                    <Box component="img" src={yannLogo} alt="YANN" sx={{ height: '100%', width: 'auto', display: 'block' }} />
                                </Box>
                            </Toolbar>
                            <Divider />

                            {/* Back button — EXACT match */}
                            <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                                <ListItemButton sx={{ borderRadius: 1.5, py: 1, px: 1.5, bgcolor: alpha('#2563ea', 0.04) }}>
                                    <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}><ArrowBackIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="All Categories" primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600, color: 'primary.main' }} />
                                    <GridViewIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.6 }} />
                                </ListItemButton>
                            </Box>

                            {/* Category gradient header — EXACT match */}
                            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                                <Box sx={{ background: tx.cat.gradient, borderRadius: 2, px: 2, py: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.01em' }}>{tx.cat.label}</Typography>
                                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontSize: '0.7rem' }}>{catItems.length} {catItems.length === 1 ? 'module' : 'modules'}</Typography>
                                </Box>
                            </Box>

                            {/* Menu items — EXACT match with real ListItem/ListItemButton/ListItemIcon/ListItemText */}
                            <List sx={{ flexGrow: 1, px: 1, pt: 0.5 }}>
                                {catItems.map((item, idx) => (
                                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5, animation: showContent ? `sidebarItemSlideIn 0.3s ease-out ${idx * 0.05}s both` : 'none' }}>
                                        <ListItemButton
                                            sx={{
                                                borderRadius: 1, px: 2,
                                                ...(idx === 0 ? { bgcolor: 'primary.light', color: 'primary.main', '& .MuiListItemIcon-root': { color: 'primary.main' } } : {}),
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                                            <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>

                            <Divider />
                            {/* Logout — EXACT match */}
                            <Box sx={{ p: 2 }}>
                                <ListItemButton sx={{ borderRadius: 1, color: 'error.main' }}>
                                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
                                    <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                                </ListItemButton>
                            </Box>
                        </Box>
                    </Box>

                    {/* ── AppBar + Content placeholder ─────────────────── */}
                    <Box sx={{ position: 'fixed', top: 0, left: SIDEBAR_W, right: 0, bottom: 0, opacity: showContent ? 1 : 0, transform: showContent ? 'translateX(0)' : 'translateX(30px)', transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1) 0.06s' }}>
                        {/* AppBar — matching real AppBar */}
                        <Box sx={{ height: 64, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>{catItems[0]?.text || tx.cat.label}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: 180, height: 36, bgcolor: '#f1f5f9', borderRadius: 1, display: { xs: 'none', sm: 'block' } }} />
                                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#f1f5f9' }} />
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                                        <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: 600, fontSize: '0.8rem' }}>Super Admin</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{admin?.email || 'admin@scankaro.com'}</Typography>
                                    </Box>
                                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563ea', fontWeight: 'bold', width: 40, height: 40, border: '1px solid #2563ea', fontSize: '0.85rem' }}>SA</Avatar>
                                </Box>
                            </Box>
                        </Box>
                        {/* Content skeleton */}
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', height: 'calc(100vh - 64px)' }}>
                            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                                {[1, 2, 3].map(i => (
                                    <Box key={i} sx={{ flex: 1, height: 110, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', animation: showContent ? `categoryFadeIn 0.4s ease-out ${0.1 + i * 0.08}s both` : 'none' }} />
                                ))}
                            </Box>
                            <Box sx={{ height: 220, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', animation: showContent ? 'categoryFadeIn 0.4s ease-out 0.4s both' : 'none' }} />
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CategoryHub;
